import React from "react";
import { Stack } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import {
  createAcademicYear,
  updateAcademicYear,
} from "../redux/actions/academicYearActions";
import { academicYearActions } from "../redux/slices/academicYear";
import { cleanFormData } from "../helpers/formHelpers";

import { AcademicYearResponseDto } from "../shared/dtos/academicYear/AcademicYearResponseDto";
import { AcademicYearRequestDto } from "../shared/dtos/academicYear/AcademicYearRequestDto";
import { AcademicYearStatus } from "../shared/enums/AcademicYearStatus";

const formatDateForInput = (dateStr?: string): string =>
  dateStr ? dateStr.substring(0, 10) : "";

const academicYearSchema = Yup.object().shape({
  name: Yup.string().required(
    "Academic year name is required (e.g., 2024-2025)",
  ),
  startDate: Yup.date().required("Start date is required"),
  endDate: Yup.date()
    .required("End date is required")
    .min(Yup.ref("startDate"), "End date must be after start date"),
  status: Yup.mixed<AcademicYearStatus>().oneOf(
    Object.values(AcademicYearStatus),
  ),
});

const statusOptions = [
  { value: AcademicYearStatus.ACTIVE, label: "Active" },
  { value: AcademicYearStatus.INACTIVE, label: "Inactive" },
  { value: AcademicYearStatus.COMPLETED, label: "Completed" },
];

interface CreateOrUpdateAcademicYearFormProps {
  academicYear?: AcademicYearResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateAcademicYearForm: React.FC<
  CreateOrUpdateAcademicYearFormProps
> = ({
  academicYear = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: AcademicYearRequestDto = {
    name: academicYear?.name || "",
    startDate: formatDateForInput(academicYear?.startDate),
    endDate: formatDateForInput(academicYear?.endDate),
    status: academicYear?.academicYearStatus || AcademicYearStatus.ACTIVE,
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(academicYearActions.setErrorUpdate(null))
            : () => dispatch(academicYearActions.setErrorCreate(null))
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={academicYearSchema}
        onSubmit={(values) => {
          const cleanedValues = cleanFormData(values) as AcademicYearRequestDto;

          if (isEditing && academicYear) {
            dispatch(
              updateAcademicYear({
                id: academicYear.id,
                values: cleanedValues,
              }),
            );
          } else {
            const { status, ...createValues } = cleanedValues;
            dispatch(
              createAcademicYear(createValues as AcademicYearRequestDto),
            );
          }
        }}
        enableReinitialize={true}
      >
        {({ dirty, isValid }: FormikProps<AcademicYearRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <FormTextField
                name="name"
                label="Academic Year Name"
                placeholder="e.g., 2024-2025"
              />

              <FormTextField
                name="startDate"
                label="Start Date"
                type="date"
                slotProps={{ inputLabel: { shrink: true } }}
              />

              <FormTextField
                name="endDate"
                label="End Date"
                type="date"
                slotProps={{ inputLabel: { shrink: true } }}
              />

              {isEditing && (
                <FormSelectField
                  name="status"
                  label="Status"
                  options={statusOptions}
                />
              )}

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Year"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateAcademicYearForm;
